export type DataGridColumn = {
  title?: string;
  name?: string;
  isRowSelector?: boolean;
  isSticky?: boolean;
  stickyLeft?: number;
  isVisible?: boolean;
  hasSorter?: boolean;
  checkRoles?: string[];
  order?: number;
  sx?: React.CSSProperties;
  width?: number;
  minWidth?: number;
};

export type SavedColumn = {
  name: string;
  width?: number;
  order: number;
  isVisible?: boolean;
};

export const stickyLeft1 = 25;
export const stickyLeft2 = 31;
export const stickyLeft3 = 58;

export const isManageableColumnName = (name?: string) =>
  name !== 'rowSelector' && name !== 'actions' && name !== 'preview';

/** Persist columns with `order` = index in the full column list (for DB + localStorage). */
export const toSavedVisibleColumns = (columns: DataGridColumn[]): SavedColumn[] =>
  columns
    .map((col, index) => ({ col, index }))
    .filter(({ col }) => col.name)
    .map(({ col, index }) => ({
      name: col.name!,
      width: col.width,
      order: index,
      isVisible: Boolean(col.isVisible),
    }));

/** Sort key based on persisted `order`, fallback to current columns list index. */
export const savedColumnSortKey = ({
  name,
  savedColumns,
  columns,
}: {
  name: string | undefined;
  savedColumns: SavedColumn[];
  columns: DataGridColumn[];
}): number => {
  if (!name) {
    return Number.MAX_SAFE_INTEGER;
  }
  const saved = savedColumns.find(s => s.name === name);
  if (saved) {
    return saved.order;
  }

  const fallbackIndex = columns.findIndex(column => column.name === name);
  return fallbackIndex === -1 ? Number.MAX_SAFE_INTEGER : fallbackIndex;
};

export const compareDataGridColumnsBySavedOrder = (
  a: DataGridColumn,
  b: DataGridColumn,
  savedColumns: SavedColumn[],
  columns: DataGridColumn[],
): number => {
  const aOrder = savedColumnSortKey({ name: a.name, savedColumns, columns });
  const bOrder = savedColumnSortKey({ name: b.name, savedColumns, columns });

  if (aOrder !== bOrder) {
    return aOrder - bOrder;
  }

  const aIsSaved = !!savedColumns.find(s => s.name === a.name);
  const bIsSaved = !!savedColumns.find(s => s.name === b.name);

  if (aIsSaved === bIsSaved) {
    return 0;
  }

  return aIsSaved ? -1 : 1;
};

export const applySavedColumnsState = ({
  columns,
  savedColumns,
}: {
  columns: DataGridColumn[];
  savedColumns: SavedColumn[];
}) => {
  if (savedColumns.length === 0) {
    return columns.map(col => ({ ...col, isVisible: col.isVisible }));
  }

  const savedColumnNames = savedColumns.map(column => column.name);

  return columns
    .map(col => {
      const savedColumn = savedColumns.find(saved => saved.name === col.name);
      return {
        ...col,
        isVisible: savedColumn ? savedColumn.isVisible !== false : savedColumnNames.includes(col.name!),
        width: savedColumn?.width ?? col.width,
      };
    })
    .sort((a, b) => compareDataGridColumnsBySavedOrder(a, b, savedColumns, columns));
};

export const reorderManageableColumnsByIndex = ({
  columns,
  sourceIndex,
  targetIndex,
}: {
  columns: DataGridColumn[];
  sourceIndex: number;
  targetIndex: number;
}) => {
  const reorderableColumns = columns.filter(col => isManageableColumnName(col.name));
  const reordered = [...reorderableColumns];
  const [moved] = reordered.splice(sourceIndex, 1);
  reordered.splice(targetIndex, 0, moved);

  let reorderedIndex = 0;

  return columns.map(col => {
    if (!isManageableColumnName(col.name)) {
      return col;
    }

    const nextColumn = reordered[reorderedIndex];
    reorderedIndex += 1;
    return nextColumn;
  });
};

/** Keep hidden columns in their saved position so re-checking restores the same location. */
export const getManageableColumnsDisplayOrder = (columns: DataGridColumn[]): DataGridColumn[] => {
  return columns.filter(col => isManageableColumnName(col.name));
};

export const reorderManageableColumnsByDisplayIndex = ({
  columns,
  sourceIndex,
  targetIndex,
}: {
  columns: DataGridColumn[];
  sourceIndex: number;
  targetIndex: number;
}): DataGridColumn[] => {
  const displayOrder = getManageableColumnsDisplayOrder(columns);
  const reordered = [...displayOrder];
  const [moved] = reordered.splice(sourceIndex, 1);
  reordered.splice(targetIndex, 0, moved);

  let reorderedIndex = 0;

  return columns.map(col => {
    if (!isManageableColumnName(col.name)) {
      return col;
    }

    const nextColumn = reordered[reorderedIndex];
    reorderedIndex += 1;
    return nextColumn;
  });
};

export const stickyCellSx = ({ left = 0 }: { left?: number }) => ({
  position: 'sticky' as const,
  left,
  zIndex: 11, // Keep sticky cells above moving header resizer lines when horizontally scrolling.
  backgroundColor: 'background.paper',
});

function blendRgba(r: number, g: number, b: number, a: number, bgR: number, bgG: number, bgB: number): string {
  const oR = Math.round(r * a + bgR * (1 - a));
  const oG = Math.round(g * a + bgG * (1 - a));
  const oB = Math.round(b * a + bgB * (1 - a));
  return `rgb(${oR}, ${oG}, ${oB})`;
}

export function getOpaqueHoverColor(mode: 'dark' | 'light'): string {
  if (mode === 'dark') {
    return blendRgba(255, 255, 255, 0.04, 0x16, 0x1b, 0x22);
  }
  return blendRgba(0, 0, 0, 0.04, 0xff, 0xff, 0xff);
}

export function getOpaqueSelectedColor(mode: 'dark' | 'light'): string {
  if (mode === 'dark') {
    return blendRgba(255, 255, 255, 0.14, 0x16, 0x1b, 0x22);
  }
  return blendRgba(0, 0, 0, 0.08, 0xff, 0xff, 0xff);
}

/**
 * Provides a temporary width for the first sticky columns when no explicit width exists.
 *
 * Why this exists:
 * - Sticky columns are positioned with fixed `left` offsets.
 * - Without a deterministic width, leading utility columns can shrink/expand and cause
 *   overlap or drift between sticky header/body cells.
 *
 * What it does:
 * - Returns `undefined` when the column already has an explicit width.
 * - For `rowSelector`, infers width from the next sticky column's `left` offset.
 * - For utility sticky columns (sticky + no title), infers width from the distance to
 *   the next sticky column.
 */
export const getLeadingStickyFallbackWidth = ({
  column,
  visibleColumns,
}: {
  column: DataGridColumn;
  visibleColumns: DataGridColumn[];
}): number | undefined => {
  // `maxWidth` alone does not guarantee stable sticky widths.
  const hasExplicitWidth =
    column.width != null || column.minWidth != null || column.sx?.width != null || column.sx?.minWidth != null;
  if (hasExplicitWidth) {
    return undefined;
  }

  const stickyLefts = visibleColumns
    .map(visibleColumn => (visibleColumn.isRowSelector ? 0 : visibleColumn.stickyLeft))
    .filter((left): left is number => typeof left === 'number')
    .sort((a, b) => a - b);

  if (column.isRowSelector) {
    const nextStickyLeft = stickyLefts.find(left => left > 0);
    return nextStickyLeft ?? stickyLeft2;
  }

  const isLeadingUtilitySticky = Boolean(column.isSticky) && !column.title;
  if (!isLeadingUtilitySticky || typeof column.stickyLeft !== 'number' || column.stickyLeft <= 0) {
    return undefined;
  }

  const currentLeft = column.stickyLeft;
  const nextStickyLeft = stickyLefts.find(left => left > currentLeft);
  if (typeof nextStickyLeft === 'number') {
    return Math.max(1, nextStickyLeft - currentLeft);
  }

  return stickyLeft3 - stickyLeft2;
};
